import { format } from 'date-fns';
import {
  ARCA_ALIQUOT_TYPE_0,
  ARCA_ALIQUOT_TYPE_10_5,
  ARCA_ALIQUOT_TYPE_21,
  ARCA_ALIQUOT_TYPE_27,
  ARCA_ALIQUOT_TYPE_2_5,
  ARCA_ALIQUOT_TYPE_5,
  ARCA_ALIQUOT_TYPE_EXENTO,
  ArcaAliquotType,
} from './arca.runtime-types';
import {
  ArcaAliquotDetail,
  ArcaLineItemsByOrder,
  ArcaProductData,
  ArcaQrCodeUrlParams,
  TaxVoucherArca,
} from './arca.types';
import { TaxVoucherTotals } from '@pedix-workspace/utils';
import { TaxVoucherOptions } from '@pedix-workspace/utils';
import { OrderItemLineItem } from '@pedix-workspace/utils';
import { AliquotAmount } from '@pedix-workspace/utils';
import { ArcaVoucherLetter } from './arca.types';
import { getArcaAliquotValue, getSanitizedAmount } from './arca.utils';

export const ARCA_CURRENCY_FORMAT_OPTIONS = {
  forceDecimals: true,
  separateSymbol: false,
  roundAmount: false,
  roundDecimals: true,
  formatThousands: false,
  skipSymbol: false,
};

export function getFormattedCaeExpirationDate(caeExpirationDate: string): string {
  return [
    caeExpirationDate.slice(0, 4),
    caeExpirationDate.slice(4, 6),
    caeExpirationDate.slice(6, 8),
  ]
    .reverse()
    .join('/');
}

export function getArcaVoucherQrUrl(taxVoucher: TaxVoucherArca): string {
  const params: ArcaQrCodeUrlParams = {
    ver: 1,
    fecha: format(taxVoucher.details.created, 'yyyy-MM-dd'),
    cuit: taxVoucher.details.accountDetails.cuit,
    ptoVta: taxVoucher.details.pointOfSale,
    tipoCmp: taxVoucher.details.type,
    nroCmp: taxVoucher.details.voucherNumber,
    importe: taxVoucher.details.voucherTotals.totalAmount,
    moneda: taxVoucher.details.currencyId,
    ctz: taxVoucher.details.exchangeRate,
    tipoDocRec: taxVoucher.details.customerDocumentType,
    nroDocRec: taxVoucher.details.customerDocumentNumber,
    tipoCodAut: 'E',
    codAut: parseInt(taxVoucher.details.cae),
  };
  const base64UrlParams = encodeBase64UrlSafe(params);

  return `https://www.afip.gob.ar/fe/qr/?p=${base64UrlParams}`;
}

export function encodeBase64UrlSafe(obj: any): string {
  const json = JSON.stringify(obj);

  let base64: string;

  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    // We are in a browser
    base64 = window.btoa(unescape(encodeURIComponent(json)));
  } else if (typeof Buffer !== 'undefined') {
    // We are in Node.js
    base64 = Buffer.from(json, 'utf-8').toString('base64');
  } else {
    throw new Error('Unsupported environment for base64 encoding');
  }

  // Make it URL-safe
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Determines grouping strategy based on parameters
 */
export function determineGroupingStrategy(options: TaxVoucherOptions) {
  if (options.groupingConceptName && options.groupByOrder) {
    return 'by-order-and-concept';
  }
  if (options.groupByOrder) {
    return 'by-order';
  }
  if (options.groupingConceptName) {
    return 'by-concept';
  }
  return 'none';
}

/**
 * Determines rendering strategy for PDF generation
 */
export function determineRenderingStrategy(
  lineItemsByOrder: TaxVoucherArca['details']['lineItemsByOrder'],
  options: TaxVoucherOptions,
) {
  if (lineItemsByOrder.length === 1) {
    return 'single-order';
  }

  if (options.groupByOrder || options.groupingConceptName) {
    return options.groupingConceptName ? 'grouped-by-concept' : 'grouped-by-order';
  }

  return 'multiple-orders-with-headers';
}

export function processLineItemsByOrder(data: {
  lineItemsByOrder: TaxVoucherArca['details']['lineItemsByOrder'];
  taxVoucherOptions: TaxVoucherArca['details']['taxVoucherOptions'];
  voucherLetter: ArcaVoucherLetter;
}): ArcaLineItemsByOrder[] {
  const groupingStrategy = determineGroupingStrategy(data.taxVoucherOptions);

  switch (groupingStrategy) {
    case 'none':
      return processWithoutGrouping(data.lineItemsByOrder, data.voucherLetter);

    case 'by-concept':
      return processWithConceptGrouping(
        data.lineItemsByOrder,
        data.taxVoucherOptions.groupingConceptName!,
        data.voucherLetter,
      );

    case 'by-order':
    case 'by-order-and-concept':
      return processWithOrderGrouping(
        data.lineItemsByOrder,
        data.taxVoucherOptions,
        data.voucherLetter,
      );

    default:
      throw new Error(`Unsupported grouping strategy: ${groupingStrategy}`);
  }
}

export function processWithoutGrouping(
  lineItemsByOrder: TaxVoucherArca['details']['lineItemsByOrder'],
  voucherLetter: ArcaVoucherLetter,
): ArcaLineItemsByOrder[] {
  return lineItemsByOrder.map(order => ({
    orderId: order.orderId,
    lineItems: convertLineItemsToArcaProducts(order.lineItems, voucherLetter),
  }));
}

/**
 * Processes orders by grouping them by concept (for single order)
 */
export function processWithConceptGrouping(
  lineItemsByOrder: TaxVoucherArca['details']['lineItemsByOrder'],
  groupingConceptName: string,
  voucherLetter: ArcaVoucherLetter,
): ArcaLineItemsByOrder[] {
  const allLineItems = lineItemsByOrder.flatMap(order => order.lineItems);

  // Group by aliquot
  const itemsByAliquot =
    voucherLetter === 'A' ? groupLineItemsByAliquot(allLineItems) : { '': allLineItems };

  const groupedLineItems = Object.entries(itemsByAliquot).map(([aliquot, lineItems]) => {
    const totalAmount = lineItems.reduce((sum, item) => sum + item.total, 0);

    return <OrderItemLineItem>{
      name: groupingConceptName,
      quantity: 1,
      unitPrice: totalAmount,
      total: totalAmount,
      aliquot: <AliquotAmount>(aliquot === 'exempt' ? 'exempt' : parseFloat(aliquot)),
    };
  });

  return [
    {
      orderId: 'grouped',
      lineItems: convertLineItemsToArcaProducts(groupedLineItems, voucherLetter),
    },
  ];
}

/**
 * Processes orders by grouping them by order
 */
export function processWithOrderGrouping(
  lineItemsByOrder: TaxVoucherArca['details']['lineItemsByOrder'],
  options: TaxVoucherArca['details']['taxVoucherOptions'],
  voucherLetter: ArcaVoucherLetter,
): ArcaLineItemsByOrder[] {
  return lineItemsByOrder.map(({ orderId, lineItems }) => {
    // Group line items by aliquot
    const itemsByAliquot =
      voucherLetter === 'A' ? groupLineItemsByAliquot(lineItems) : { '': lineItems };

    const groupedLineItems = Object.entries(itemsByAliquot).map(([aliquot, items]) => {
      const totalAmount = items.reduce((sum, item) => sum + item.total, 0);

      // Determine concept name
      let conceptName = `Pedido #${orderId}`;
      if (options.groupingConceptName) {
        conceptName += ` - ${options.groupingConceptName}`;
      }

      return <OrderItemLineItem>{
        name: conceptName,
        quantity: 1,
        unitPrice: totalAmount,
        total: totalAmount,
        aliquot: <AliquotAmount>(aliquot === 'exempt' ? 'exempt' : parseFloat(aliquot)),
      };
    });

    return {
      orderId,
      lineItems: convertLineItemsToArcaProducts(groupedLineItems, voucherLetter),
    };
  });
}

/**
 * Groups line items by aliquot
 */
export function groupLineItemsByAliquot(
  lineItems: OrderItemLineItem[],
): Record<string, OrderItemLineItem[]> {
  return lineItems.reduce(
    (acc, item) => {
      const key = item.aliquot.toString();
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, OrderItemLineItem[]>,
  );
}

/**
 * Converts line items to Arca products format
 */
export function convertLineItemsToArcaProducts(
  lineItems: OrderItemLineItem[],
  voucherLetter: ArcaVoucherLetter,
): ArcaProductData[] {
  return lineItems.map(lineItem => {
    if (voucherLetter === 'A') {
      const aliquotDetailsForUnitPrice = getAliquotAndBaseAmounts(
        lineItem.unitPrice,
        lineItem.aliquot,
      );
      const aliquotDetailsForItemTotal = getAliquotAndBaseAmounts(lineItem.total, lineItem.aliquot);

      return {
        name: lineItem.name,
        quantity: lineItem.quantity,
        unitPrice: aliquotDetailsForUnitPrice.aliquotBaseAmount,
        aliquot: lineItem.aliquot,
        aliquotAmount: aliquotDetailsForItemTotal.aliquotAmount,
        aliquotBaseAmount: aliquotDetailsForItemTotal.aliquotBaseAmount,
        totalAmount: lineItem.total,
      };
    }

    return {
      name: lineItem.name,
      quantity: lineItem.quantity,
      unitPrice: lineItem.unitPrice,
      aliquot: 0,
      aliquotAmount: 0,
      aliquotBaseAmount: 0,
      totalAmount: lineItem.total,
    };
  });
}

function getAliquotAndBaseAmounts(
  totalAmount: number,
  aliquot: number | 'exempt',
): { aliquotBaseAmount: number; aliquotAmount: number } {
  if (aliquot === 'exempt') {
    // IVA Exento
    return {
      aliquotAmount: 0,
      aliquotBaseAmount: totalAmount,
    };
  } else {
    const aliquotAmount = (totalAmount * aliquot) / (100 + aliquot);

    return {
      aliquotAmount,
      aliquotBaseAmount: totalAmount - aliquotAmount,
    };
  }
}

/**
 * Calculates IVA amount for a specific aliquot rate
 */
export function getLineItemsAliquotSumAmount(
  lineItems: OrderItemLineItem[],
  aliquotType: ArcaAliquotType,
): ArcaAliquotDetail | null {
  const aliquot = getArcaAliquotValue(aliquotType);

  const filteredLineItems = lineItems.filter(lineItem => lineItem.aliquot === aliquot);

  if (filteredLineItems.length === 0) {
    return null;
  }

  return filteredLineItems.reduce(
    (aliquotDetails, lineItem) => {
      const { aliquotAmount, aliquotBaseAmount } = getAliquotAndBaseAmounts(
        lineItem.total,
        aliquot,
      );

      aliquotDetails.aliquotAmountSum += aliquotAmount;
      aliquotDetails.aliquotBaseAmountSum += aliquotBaseAmount;
      aliquotDetails.totalAmountSum += lineItem.total;

      return aliquotDetails;
    },
    {
      aliquotType,
      aliquot,
      aliquotAmountSum: 0,
      aliquotBaseAmountSum: 0,
      totalAmountSum: 0,
    },
  );
}

export function calculateVoucherTotals(
  lineItemsByOrder: TaxVoucherArca['details']['lineItemsByOrder'],
  voucherLetter: ArcaVoucherLetter,
): TaxVoucherTotals {
  // Flatten all line items for calculation
  const allLineItems = lineItemsByOrder.flatMap(order => order.lineItems);

  const total = allLineItems.reduce((acc, lineItem) => acc + lineItem.total, 0);

  const totalAmount = getSanitizedAmount(total);

  if (voucherLetter === 'A' || voucherLetter === 'B') {
    const aliquotTotals = [
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_0),
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_2_5),
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_5),
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_10_5),
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_21),
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_27),
    ].filter(aliquotDetails => aliquotDetails !== null);

    const ivaTotal = aliquotTotals.reduce(
      (acc, aliquotDetails) => acc + aliquotDetails.aliquotAmountSum,
      0,
    );
    const ivaExento =
      getLineItemsAliquotSumAmount(allLineItems, ARCA_ALIQUOT_TYPE_EXENTO)?.totalAmountSum || 0;
    const subtotal = total - ivaTotal - ivaExento;

    const vatAmount = getSanitizedAmount(ivaTotal);
    const netAmount = getSanitizedAmount(subtotal);
    const exemptAmount = getSanitizedAmount(ivaExento);
    const vatDetails: TaxVoucherTotals['vatDetails'] = aliquotTotals
      .filter(aliquotDetails => aliquotDetails.aliquot !== 'exempt')
      .map(aliquotDetails => ({
        id: aliquotDetails.aliquotType,
        aliquot: <number>aliquotDetails.aliquot,
        baseAmount: getSanitizedAmount(aliquotDetails.aliquotBaseAmountSum),
        vatAmount: getSanitizedAmount(aliquotDetails.aliquotAmountSum),
      }));

    return {
      vatAmount,
      netAmount,
      exemptAmount,
      vatDetails,
      totalAmount,
      tributesAmount: 0,
    };
  }

  return {
    vatAmount: 0,
    netAmount: totalAmount,
    exemptAmount: 0,
    vatDetails: undefined,
    totalAmount,
    tributesAmount: 0,
  };
}
